import type { ReactNode, Ref } from 'react';
import type { EmailThreadEmail } from '@erp/rpc';
import { T, Plural, Num, Var, useGT } from 'gt-react';
import { PaperclipIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useEmailCidMap } from '@/components/email-viewer/use-email-cid-map';
import { EmailToggleButton } from '@/components/email-viewer/email-toggle-button';
import { EmailBodyView } from '@/components/email-viewer/email-body-content';
import { cn } from '@/lib/utils';
import { CollapsedEmailSummary } from '@/components/email-viewer/collapsed-email-summary';

interface EmailCardProps {
  email: EmailThreadEmail;
  isExpanded: boolean;
  isHighlighted: boolean;
  splitDocuments?: ReactNode;
  onToggle: () => void;
  onAttachmentClick: (documentId: string) => void;
  ref?: Ref<HTMLDivElement>;
}

export function ActivityEmailCard({
  email,
  isExpanded,
  isHighlighted,
  splitDocuments,
  onToggle,
  onAttachmentClick,
  ref,
}: EmailCardProps) {
  const gt = useGT();
  const cidMap = useEmailCidMap(email);
  const attachmentCount =
    (email.attachments?.length ?? 0) +
    (email.documentAssociations?.length ?? 0);
  return (
    <div
      ref={ref}
      className={cn(
        'rounded-md border transition-all',
        isHighlighted
          ? 'border-primary border-2 bg-primary/5'
          : 'border-border/50 bg-muted/30',
      )}
    >
      {!isExpanded ? (
        // Collapsed view
        <CollapsedEmail
          email={email}
          attachmentCount={attachmentCount}
          onToggle={onToggle}
        />
      ) : (
        // Expanded view
        <div className="p-3 space-y-2">
          {/* Header */}
          <div>
            <div className="flex items-start justify-between gap-2">
              <h4 className="font-semibold text-sm flex-1">
                {email.subject ||
                  gt('No Subject', {
                    context: 'email missing subject placeholder',
                  })}
              </h4>
              <EmailToggleButton
                isExpanded={isExpanded}
                onToggle={onToggle}
                className="shrink-0"
              />
            </div>
            <div className="flex items-center justify-between gap-2 mt-1">
              <div className="text-xs text-muted-foreground space-y-0.5">
                <div>
                  <span className="font-medium">
                    {gt('From:', {
                      context: 'email header From label',
                    })}
                  </span>{' '}
                  {email.from}
                </div>
                <div>
                  <span className="font-medium">
                    {gt('To:', { context: 'email header To label' })}
                  </span>{' '}
                  {email.to}
                </div>
              </div>
              <span className="text-xs text-muted-foreground shrink-0">
                {new Date(email.createdAt).toLocaleString()}
              </span>
            </div>
          </div>

          {/* Body */}
          <div className="pt-2 border-t">
            <EmailBodyView email={email} cidMap={cidMap} />
          </div>

          {/* Attachments */}
          <EmailAttachments
            email={email}
            onAttachmentClick={onAttachmentClick}
          />

          {/* Split-child documents (opt-in, fetched on demand) */}
          {splitDocuments}
        </div>
      )}
    </div>
  );
}

function CollapsedEmail({
  email,
  attachmentCount,
  onToggle,
}: Pick<EmailCardProps, 'email' | 'onToggle'> & { attachmentCount: number }) {
  const gt = useGT();
  return (
    <div className="p-3">
      <CollapsedEmailSummary
        subject={
          email.subject ||
          gt('No Subject', { context: 'email missing subject placeholder' })
        }
        from={email.from}
        receivedAt={new Date(email.createdAt)}
        attachmentCount={attachmentCount}
        onToggle={onToggle}
      />
    </div>
  );
}

function EmailAttachments({
  email,
  onAttachmentClick,
}: Pick<EmailCardProps, 'email' | 'onAttachmentClick'>) {
  const documentAssociations = email.documentAssociations?.filter(
    ({ document }) =>
      !(document.contentId && document.mimeType?.startsWith('image/')),
  );
  const attachmentCount =
    (email.attachments?.length ?? 0) + (documentAssociations?.length ?? 0);
  return (
    <>
      {attachmentCount > 0 && (
        <div className="pt-2 border-t">
          <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-2">
            <PaperclipIcon size={12} />
            <span>
              <T context="email attachment count">
                <Num>{attachmentCount}</Num>{' '}
                <Plural
                  n={attachmentCount}
                  one="attachment"
                  other="attachments"
                />
              </T>
            </span>
          </div>
          <div className="space-y-1">
            {/* Document associations */}
            {documentAssociations?.map((assoc) => (
              <DocumentButton
                key={assoc.id}
                association={assoc}
                onOpen={onAttachmentClick}
              />
            ))}
            {/* Legacy attachments */}
            {email.attachments?.map((att) => (
              <button
                key={att.id}
                type="button"
                className="flex items-center gap-2 w-full text-left text-sm p-2 rounded hover:bg-muted/50 transition-colors"
                onClick={() => onAttachmentClick(att.id)}
              >
                <PaperclipIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate">{att.fileName}</span>
                <span className="text-xs text-muted-foreground shrink-0">
                  <T context="legacy attachment file size">
                    <Var>{`${(att.sizeBytes / 1024).toFixed(1)} KB`}</Var>
                  </T>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

export function ActivityEmailSplitDocuments({
  email,
  showSplitDocuments,
  onToggleSplit,
  onAttachmentClick,
  isFetching,
}: {
  email: EmailThreadEmail;
  showSplitDocuments: boolean;
  isFetching: boolean;
  onToggleSplit: () => void;
  onAttachmentClick: (documentId: string) => void;
}) {
  return (
    <>
      {(email.splitDocumentCount ?? 0) > 0 && (
        <div className="pt-2 border-t">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={(e) => {
              e.stopPropagation();
              onToggleSplit();
            }}
          >
            {showSplitDocuments ? (
              <T context="button label to hide split-child documents of an email">
                Hide split documents
              </T>
            ) : (
              <T context="button label to show split-child documents of an email">
                Show <Num>{email.splitDocumentCount ?? 0}</Num>{' '}
                <Plural
                  n={email.splitDocumentCount ?? 0}
                  one="split document"
                  other="split documents"
                />
              </T>
            )}
          </Button>
          {showSplitDocuments && (
            <SplitDocumentList
              associations={email.splitDocumentAssociations}
              isFetching={isFetching}
              onAttachmentClick={onAttachmentClick}
            />
          )}
        </div>
      )}
    </>
  );
}

function DocumentButton({
  association,
  onOpen,
}: {
  association: NonNullable<EmailThreadEmail['documentAssociations']>[number];
  onOpen: (id: string) => void;
}) {
  return (
    <button
      type="button"
      className="flex items-center gap-2 w-full text-left text-sm p-2 rounded hover:bg-muted/50 transition-colors"
      onClick={() => onOpen(association.document.id)}
    >
      <PaperclipIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="truncate">
        {association.document.originalFileName || association.document.name}
      </span>
      <span className="text-xs text-muted-foreground shrink-0">
        {association.document.fileSizeBytes ? (
          <T context="attachment file size kilobytes">
            <Var>
              {`${(association.document.fileSizeBytes / 1024).toFixed(1)} KB`}
            </Var>
          </T>
        ) : (
          ''
        )}
      </span>
    </button>
  );
}

function SplitDocumentList({
  associations,
  isFetching,
  onAttachmentClick,
}: {
  associations: EmailThreadEmail['splitDocumentAssociations'];
  isFetching: boolean;
  onAttachmentClick: (id: string) => void;
}) {
  return associations?.length ? (
    <div className="mt-1 space-y-1 rounded-md border border-dashed p-1">
      {associations.map((assoc) => (
        <DocumentButton
          key={assoc.id}
          association={assoc}
          onOpen={onAttachmentClick}
        />
      ))}
    </div>
  ) : isFetching ? (
    <p className="text-xs text-muted-foreground italic p-2">
      <T context="email viewer, loading split documents">
        Loading split documents...
      </T>
    </p>
  ) : (
    <p className="text-xs text-muted-foreground italic p-2">
      <T context="email viewer, no split documents found">
        No split documents found
      </T>
    </p>
  );
}
